import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useNoScroll } from '../hooks/useNoScroll.js';
import DrawingCanvas from '../components/DrawingCanvas.jsx';
import { pickPromptsRandom } from '../shared/prompts.js';
import { NPC_PRESETS, NPC_TEXT_ANSWERS, pickRandom, rollNpcScore } from '../shared/npc-data.js';

const ANSWER_TIME = 30;
const EMOJIS = ['😀','😎','🥳','🤔','😴','🙈','🔥','⚡','🌸','🎯','🦊','🐼','🍜','🎸','🚀'];

const SCORE_HINTS = [
  '伝わったか？ → ★5',
  '味があるか？ → +1',
  'とりあえず描いた → ★2',
  '上手くなくても面白ければ★4！',
  '何を描いたか不明 → ★1',
];

export default function SoloScreen() {
  const [, setLocation] = useLocation();
  const [phase, setPhase] = useState('setup'); // setup | answer | selfScore | npcReveal | final
  useNoScroll(phase === 'answer');
  const [name, setName] = useState(() => localStorage.getItem('saiten-man-solo-name') ?? '');
  const [emoji, setEmoji] = useState(() => localStorage.getItem('saiten-man-solo-emoji') ?? '😀');
  const [totalRounds, setTotalRounds] = useState(3);
  const [npcCount, setNpcCount] = useState(2);
  const [npcs, setNpcs] = useState([]); // [{nickname,emoji,total}]
  const [prompts, setPrompts] = useState([]);
  const [roundIdx, setRoundIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ANSWER_TIME);
  const [scores, setScores] = useState([]); // 自分のラウンドごとスコア
  const [npcRoundData, setNpcRoundData] = useState([]); // [{answer, stars}] 当ラウンドのNPC回答
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(0);

  const currentPrompt = prompts[roundIdx] ?? null;

  const startGame = () => {
    if (!name.trim()) return;
    localStorage.setItem('saiten-man-solo-name', name.trim());
    localStorage.setItem('saiten-man-solo-emoji', emoji);
    const picked = pickPromptsRandom(totalRounds);
    setPrompts(picked);
    setRoundIdx(0);
    setScores([]);
    setNpcRoundData([]);
    setTimeLeft(ANSWER_TIME);
    // NPC を npcCount 人生成
    const shuffled = [...NPC_PRESETS].sort(() => Math.random() - 0.5).slice(0, npcCount);
    setNpcs(shuffled.map((n) => ({ ...n, total: 0 })));
    setPhase('answer');
  };

  // タイマー
  useEffect(() => {
    if (phase !== 'answer') return;
    startTimeRef.current = performance.now();
    const tick = (now) => {
      const elapsed = Math.floor((now - startTimeRef.current) / 1000);
      const left = Math.max(0, ANSWER_TIME - elapsed);
      setTimeLeft(left);
      if (left > 0) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setPhase('selfScore');
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, roundIdx]);

  const submitAnswer = () => {
    cancelAnimationFrame(rafRef.current);
    setPhase('selfScore');
  };

  const recordSelfScore = (stars) => {
    const newScores = [...scores, stars];
    setScores(newScores);
    // NPC のラウンドスコアを生成
    const npcResults = npcs.map((n) => ({
      nickname: n.nickname,
      emoji: n.emoji,
      answer: pickRandom(NPC_TEXT_ANSWERS),
      stars: rollNpcScore(),
    }));
    setNpcRoundData(npcResults);
    // NPC 累計スコア更新
    setNpcs((prev) => prev.map((n, i) => ({ ...n, total: n.total + npcResults[i].stars })));

    if (npcs.length === 0) {
      // NPC がいなければそのまま次へ
      advanceRound(newScores);
    } else {
      setPhase('npcReveal');
    }
  };

  const advanceRound = (currentScores = scores) => {
    if (roundIdx + 1 < prompts.length) {
      setRoundIdx((i) => i + 1);
      setTimeLeft(ANSWER_TIME);
      setNpcRoundData([]);
      setPhase('answer');
    } else {
      const total = currentScores.reduce((a, b) => a + b, 0);
      const best = Number(localStorage.getItem('saiten-man-best-solo') ?? '0');
      if (total > best) localStorage.setItem('saiten-man-best-solo', String(total));
      setPhase('final');
    }
  };

  if (phase === 'setup') {
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-6 p-6">
        <div className="flex flex-col items-center gap-1">
          <p className="tv-telop text-sm">1人で遊ぶ</p>
          <h1 className="tv-heading text-3xl">ソロ採点マン</h1>
        </div>
        <div className="tv-panel w-full max-w-sm flex flex-col gap-5 p-6">
          <div>
            <label className="font-heading text-sm block mb-1">あなたの名前</label>
            <input
              className="w-full border-2 border-tv-ink rounded-lg px-3 py-2 font-body text-lg"
              maxLength={20}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: たろう"
            />
          </div>
          <div>
            <label className="font-heading text-sm block mb-2">絵文字を選ぼう</label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  className={`text-2xl w-10 h-10 rounded-lg border-2 ${emoji === e ? 'border-tv-yellow bg-yellow-50' : 'border-transparent'}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-heading text-sm block mb-2">ラウンド数</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setTotalRounds(n)}
                  className={`tv-btn px-4 py-2 text-lg ${totalRounds === n ? 'tv-btn-yellow' : 'tv-btn-white'}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-heading text-sm block mb-2">🤖 NPC 対戦相手</label>
            <div className="flex gap-2">
              {[0, 1, 2, 3].map((n) => (
                <button
                  key={n}
                  onClick={() => setNpcCount(n)}
                  className={`tv-btn px-4 py-2 text-lg ${npcCount === n ? 'tv-btn-yellow' : 'tv-btn-white'}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="font-body text-xs text-tv-ash mt-1">NPCも回答してくる。順位を競おう！</p>
          </div>
          <button
            className="tv-btn tv-btn-red text-xl py-3 mt-2 disabled:opacity-40"
            disabled={!name.trim()}
            onClick={startGame}
          >
            🎯 スタート
          </button>
        </div>
        <button onClick={() => setLocation('/')} className="tv-btn tv-btn-white px-4 py-2 text-sm">← 戻る</button>
      </div>
    );
  }

  if (phase === 'answer' && currentPrompt) {
    const timerColor = timeLeft <= 10 ? 'text-red-500' : timeLeft <= 20 ? 'text-orange-500' : 'text-tv-ink';
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center gap-4 p-4">
        <div className="w-full max-w-lg flex items-center justify-between mt-2">
          <p className="tv-telop text-sm">ラウンド {roundIdx + 1} / {prompts.length}</p>
          <p className={`font-heading text-3xl tabular-nums ${timerColor}`}>{timeLeft}s</p>
        </div>

        <div className="tv-panel w-full max-w-lg p-5 flex flex-col items-center gap-2">
          <p className="font-body text-xs text-tv-ash">お題</p>
          <p className="tv-heading text-2xl text-center">{currentPrompt.text}</p>
          <span className="text-xs font-body px-2 py-0.5 bg-tv-yellow rounded-full">🎨 お絵かき</span>
        </div>

        <div className="w-full max-w-lg">
          <DrawingCanvas ref={canvasRef} />
        </div>

        <button
          className="tv-btn tv-btn-blue text-lg px-8 py-3 w-full max-w-lg"
          onClick={submitAnswer}
        >
          ✅ 回答する
        </button>
      </div>
    );
  }

  if (phase === 'selfScore') {
    const hintList = SCORE_HINTS;
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-6 p-6">
        <div className="tv-panel w-full max-w-sm flex flex-col gap-4 p-6">
          <p className="tv-heading text-xl text-center">自己採点タイム！</p>
          <div className="bg-yellow-50 border border-tv-yellow rounded-xl p-3 flex flex-col gap-1">
            <p className="font-heading text-sm">採点ヒント</p>
            {hintList.map((h, i) => (
              <p key={i} className="font-body text-sm text-tv-ash">・{h}</p>
            ))}
          </div>
          <p className="font-body text-sm text-center text-tv-ash">あなたの回答に何★を付ける？</p>
          <div className="flex justify-center gap-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                onClick={() => recordSelfScore(s)}
                className="tv-btn tv-btn-yellow text-2xl w-12 h-12 flex items-center justify-center rounded-full"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'npcReveal') {
    const myStars = scores[scores.length - 1] ?? 0;
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-4 p-6">
        <p className="tv-heading text-2xl">NPC の回答発表！</p>
        <div className="tv-panel w-full max-w-md p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3 bg-yellow-50 rounded-lg p-2">
            <span className="text-2xl">{emoji}</span>
            <span className="font-body flex-1">{name}（あなた）</span>
            <span className="font-heading text-tv-yellow">★{myStars}</span>
          </div>
          {npcRoundData.map((n, i) => (
            <div key={i} className="border-t border-tv-ash/30 pt-2 flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{n.emoji}</span>
                <span className="font-body flex-1">{n.nickname}</span>
                <span className="font-heading text-tv-yellow">★{n.stars}</span>
              </div>
              <p className="font-body text-sm text-tv-ash pl-10">「{n.answer}」</p>
            </div>
          ))}
        </div>
        <button
          className="tv-btn tv-btn-blue text-lg py-3 px-8"
          onClick={() => advanceRound()}
        >
          {roundIdx + 1 < prompts.length ? '次のラウンドへ ▶' : '結果を見る 🏁'}
        </button>
      </div>
    );
  }

  if (phase === 'final') {
    const myTotal = scores.reduce((a, b) => a + b, 0);
    const all = [
      { nickname: name, emoji, total: myTotal, isMe: true },
      ...npcs.map((n) => ({ nickname: n.nickname, emoji: n.emoji, total: n.total, isMe: false })),
    ].sort((a, b) => b.total - a.total);
    const myRank = all.findIndex((p) => p.isMe) + 1;
    const max = prompts.length * 5;

    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-5 p-6">
        <div className="flex flex-col items-center gap-2">
          <p className="text-5xl">{emoji}</p>
          <p className="tv-heading text-2xl">{name}</p>
        </div>
        {npcs.length > 0 && (
          <p className="tv-telop text-lg">
            {myRank === 1 ? '🏆 優勝！' : `${myRank}位 / ${all.length}人中`}
          </p>
        )}
        <div className="tv-panel w-full max-w-sm flex flex-col items-center gap-2 p-5">
          <p className="tv-heading text-4xl">{myTotal} / {max} pt</p>
          <div className="flex gap-1">
            {scores.map((s, i) => (
              <span key={i} className="text-lg">
                {'★'.repeat(s)}{'☆'.repeat(5 - s)}
              </span>
            ))}
          </div>
        </div>

        {npcs.length > 0 && (
          <div className="tv-panel w-full max-w-sm p-4 flex flex-col gap-2">
            <p className="font-heading text-sm">最終順位</p>
            {all.map((p, i) => (
              <div key={i} className={`flex items-center gap-2 p-1 rounded ${p.isMe ? 'bg-yellow-50' : ''}`}>
                <span className="font-heading w-6 text-center">{i + 1}</span>
                <span className="text-xl">{p.emoji}</span>
                <span className="font-body flex-1">{p.nickname}{p.isMe && '（あなた）'}</span>
                <span className="font-heading">{p.total}pt</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 w-full max-w-sm">
          <button
            className="tv-btn tv-btn-yellow text-lg py-3"
            onClick={() => { setPhase('setup'); setScores([]); }}
          >
            🔄 もう一度
          </button>
          <button onClick={() => setLocation('/')} className="tv-btn tv-btn-white py-2 text-sm">← トップへ</button>
        </div>
      </div>
    );
  }

  return null;
}
